import { useRef, useState } from "react";
import { TransitionGroup } from "react-transition-group";

import * as styles from "./_slider.style";
import { Bullets } from "./atom.bullets";
import { Chevrons } from "./atom.chevrons";
import { Slide } from "./atom.slide";
import { SlideCaption } from "./atom.slide-caption";
import { useDragSlide } from "./hook.drag-slide";
import { Slide as SlideModel, SliderModel } from "@courses-pm2/api";
import { SizeTransition } from "@pm2/compat";

export const SliderComponentSelector = "slider-component";

type Props = {
  model: SliderModel;
  displayCaption: boolean;

  onDragStart?(): void;
  onDragEnd?(): void;
};

export enum ImageStates {
  NO_IMAGE_LOADED,
  SUCCESS,
  ERROR,
}

export function SingularSlider(props: Props): React.JSX.Element | null {
  const sliderRef = useRef<HTMLDivElement>(null);

  const [index, setIndex] = useState(0);
  const [, dragProps] = useDragSlide(sliderRef, {
    amountOfSlides: props.model.slides.length,
    index,
    onChange: setIndex,
    onDragStart: props.onDragStart,
    onDragEnd: props.onDragEnd,
  });

  const currentSlide = props.model.slides[index];

  const aspectRatio = Math.max(
    ...props.model.slides.map((it) => it.height / it.width),
  );

  const firstSlide = props.model.slides?.[props.model.slides.length - 1];
  const lastSlide = props.model.slides?.[0];

  return (
    <>
      <div className={styles.sliderWrapper}>
        <div
          {...dragProps}
          className={styles.sliderContainer}
          style={{
            aspectRatio: `1 / ${aspectRatio}`,
          }}
        >
          <div
            ref={sliderRef}
            className={styles.slider}
            style={{
              transform: `translateX(-${index * 100}%)`,
            }}
          >
            {firstSlide && (
              <Slide index={-1} model={firstSlide} isCurrent={false} />
            )}
            {props.model.slides?.map((slide, index) => (
              <Slide
                key={index}
                index={index}
                model={slide}
                isCurrent={currentSlide === slide}
              />
            ))}
            {lastSlide && (
              <Slide
                index={props.model.slides.length}
                model={lastSlide}
                isCurrent={false}
              />
            )}
          </div>
        </div>

        {props.model.slides.length > 1 && (
          <Chevrons
            onPrevClick={() =>
              setIndex((prevIndex) =>
                prevIndex > 0 ? prevIndex - 1 : props.model.slides.length - 1,
              )
            }
            onNextClick={() =>
              setIndex(
                (prevIndex) => (prevIndex + 1) % props.model.slides.length,
              )
            }
          />
        )}

        {props.model.slides.length > 1 &&
          props.model.bulletType !== "thumb" && (
            <Bullets
              slides={props.model.slides}
              activeIndex={index}
              bulletType={props.model.bulletType}
              onBulletClick={setIndex}
            />
          )}
      </div>

      {props.displayCaption && (
        <TransitionableSlideCaption
          slide={currentSlide}
          transitionKey={String(index)}
        />
      )}

      {props.model.slides.length > 1 && props.model.bulletType === "thumb" && (
        <Bullets
          slides={props.model.slides}
          activeIndex={index}
          bulletType={props.model.bulletType}
          onBulletClick={setIndex}
        />
      )}
    </>
  );
}

type SlideCaptionProps = {
  transitionKey: string;
  slide: SlideModel | undefined;
};

function TransitionableSlideCaption(props: SlideCaptionProps) {
  return (
    <TransitionGroup className={styles.imageCaptionWrapper}>
      <SizeTransition
        key={props.slide?.src}
        axis="vertical"
        swappable
        classNames={styles.imageCaptionTransition}
        timeout={styles.CAPTION_TRANSITION_DURATION_MS}
      >
        {(ref) => <SlideCaption ref={ref} caption={props.slide?.caption} />}
      </SizeTransition>
    </TransitionGroup>
  );
}
